import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SocialLogin } from '@capgo/capacitor-social-login';
import SettingRow from '../SettingRow/SettingRow';
import Backend from '../../../backend';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import _ from 'lodash';

const GoogleBindSetting = ({ intl, userDetails, updateUserDetails }) => {
  const [bindLoading, setBindLoading] = useState(false);

  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    SocialLogin.initialize({
      google: {
        webClientId: process.env.GOOGLE_CLIENT_ID,
        iOSClientId: process.env.GOOGLE_IOS_CLIENT_ID
      }
    });
  }, []);

  const isBound = !!_.get(userDetails, 'googleUserId');

  const handleUnbind = useCallback(async () => {
    try {
      const res = await fetch(Backend.api('unbindGoogle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'google-unbound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ googleUserId: null });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: 'google-unbind-failed' })), { className: 'new-toast' });
      }
    } catch (error) {
      console.error('Google unbind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'google-unbind-failed' })), { className: 'new-toast' });
    }
  }, [intl, updateUserDetails]);

  const handleBind = useCallback(async () => {
    try {
      setBindLoading(true);
      const result = await SocialLogin.login({
        provider: 'google',
        options: { scopes: ['email', 'profile'] }
      });
      const idToken = result && result.result && result.result.idToken;

      if (!idToken) {
        toast.info(IconToast('error', intl.formatMessage({ id: 'google-authentication-failed' })), {
          className: 'new-toast'
        });
        setBindLoading(false);
        return;
      }

      const res = await fetch(Backend.api('bindGoogle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ googleIdToken: idToken })
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'google-bound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ googleUserId: payload.googleUserId });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: payload.message || 'google-bind-failed' })), {
          className: 'new-toast'
        });
      }
    } catch (error) {
      console.error('Google bind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'google-bind-failed' })), { className: 'new-toast' });
    } finally {
      setBindLoading(false);
    }
  }, [intl, updateUserDetails]);

  return (
    <SettingRow
      label={intl.formatMessage({ id: 'google-account' })}
      value={
        isBound ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            {intl.formatMessage({ id: 'bound' })}
          </Box>
        ) : (
          intl.formatMessage({ id: 'not-bound' })
        )
      }
      action={
        <Button
          variant={isBound ? 'text' : 'outlined'}
          size="small"
          onClick={isBound ? handleUnbind : handleBind}
          color={isBound ? 'inherit' : 'primary'}
          disabled={bindLoading}
          sx={{ minWidth: 42, fontSize: 12, px: 1, py: 0.25, color: isBound ? 'text.secondary' : undefined }}
        >
          {bindLoading ? <CircularProgress size={16} /> : intl.formatMessage({ id: isBound ? 'unbind' : 'bind' })}
        </Button>
      }
    />
  );
};

export default GoogleBindSetting;
